import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

interface CTAAction {
  label: ReactNode;
  to?: string;
  href?: string;
  external?: boolean;
  onClick?: () => void;
}

interface CTASectionProps {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  primary?: CTAAction;
  secondary?: CTAAction;
  className?: string;
}

function Action({ action, variant }: { action: CTAAction; variant: 'primary' | 'secondary' }) {
  const base = 'inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl text-sm font-semibold transition-all';
  const cls =
    variant === 'primary'
      ? `${base} bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20`
      : `${base} bg-background text-foreground border border-border hover:border-foreground/40 hover:bg-accent`;

  const content = (
    <>
      {action.label}
      {variant === 'primary' && <ArrowRight className="h-4 w-4" />}
    </>
  );

  if (action.external && action.href) {
    return <a href={action.href} target="_blank" rel="noopener noreferrer" className={cls}>{content}</a>;
  }
  if (action.href) return <a href={action.href} className={cls}>{content}</a>;
  if (action.to) return <Link to={action.to} className={cls}>{content}</Link>;
  return <button type="button" className={cls} onClick={action.onClick}>{content}</button>;
}

export default function CTASection({
  eyebrow,
  title,
  description,
  primary,
  secondary,
  className = '',
}: CTASectionProps) {
  return (
    <section className={`relative py-16 md:py-20 px-4 sm:px-6 lg:px-8 ${className}`}>
      <div className="relative max-w-4xl mx-auto rounded-3xl border border-border bg-gradient-to-br from-primary/5 via-card to-card overflow-hidden">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--primary) / 0.1) 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
        <div className="relative px-6 py-12 md:px-12 md:py-16 text-center">
          {eyebrow && (
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-primary/10 text-primary text-xs font-medium tracking-wide uppercase">
              {eyebrow}
            </div>
          )}
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            {title}
          </h2>
          {description && (
            <p className="mt-4 max-w-2xl mx-auto text-base md:text-lg text-muted-foreground leading-relaxed">
              {description}
            </p>
          )}
          {(primary || secondary) && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              {primary && <Action action={primary} variant="primary" />}
              {secondary && <Action action={secondary} variant="secondary" />}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
